import { useCallback, useMemo, useRef, forwardRef } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { chain, isFunction, map, snakeCase, some, size, noop, kebabCase, get } from "lodash"
import useResizeObserver from "hooks/resize-observer"
import { Table as MUITable, TableHead, TableRow, TableCell, Checkbox, TableBody, TableFooter } from "@mui/material"
import { OfflineBolt } from "@mui/icons-material"
import TablePagination from "components/material/table-pagination"
import OPTooltip from "components/material/tooltip"
import { TABLE_GROUP_DROPDOWN } from "helpers/shapes"
import { DESC, getNextSort } from "helpers/sorts"
import Row from "../row"
import CellTooltip from "../cell-tooltip"
import SortLabelWithTooltip from "components/sort-label-with-tooltip"
import { recursiveStickyCheck, handleResizeObserver } from "../helpers"

import "./styles.scss"

const A_BEFORE_B = -1
const A_AFTER_B = 1

const Table = forwardRef((props, ref) => {
  const {
    selectAllSecurities,
    foundSecurities = [],
    columns,
    columnOrder,
    currentPageSelectionStatus,
    setSort,
    count,
    page,
    setPage,
    status,
    selectSecurity,
    selectedSecurities,
    openSecurityProfile,
    openFactorProfile,
    openCompositeSecurity,
    disableColumnsSelection,
    disableRowSelection,
    allowIdSort,
    idSort,
    aggregatedRow,
    rowIdClickOverride,
    groupDropdown,
    noSortLabels,
    dispatch,
    disableCategories,
    isActive,
    outsidePagination,
    onRowClick,
    onChange,
    className,
    rowsPerPage = 200,
    children,
    onScroll,
    onClick = noop,
    id,
  } = props

  const selectSecurities = useCallback(({ target: { checked } }) => {
    selectAllSecurities(foundSecurities, checked)
  }, [foundSecurities, selectAllSecurities])

  const idCell = useMemo(() => {
    const label = "ID"
    if (allowIdSort) {
      return <SortLabelWithTooltip
        noSortLabels={noSortLabels}
        active={!!idSort}
        alphabetical
        direction={idSort || DESC}
        onClick={() => {
          setSort({
            id: "id",
            ...getNextSort({ direction: idSort }),
          })
        }}
      >
        {label}
      </SortLabelWithTooltip>
    }

    if (!disableColumnsSelection) {
      return <div className="checkbox-wrapper">
        <Checkbox
          color="primary"
          className="watchlist-checkbox"
          checked={currentPageSelectionStatus === "all"}
          onChange={selectSecurities}
          indeterminate={currentPageSelectionStatus === "intermediate"}
        />
        {label}
      </div>
    }
    return label
  }, [allowIdSort, currentPageSelectionStatus, disableColumnsSelection, idSort, noSortLabels, selectSecurities, setSort])

  const numberOfStickyColumns = chain(columns)
    .flatMap("columns")
    .filter("isSticky")
    .size()
    .value()

  const sortedColumns = useMemo(() => {
    if (!aggregatedRow) {
      return columns
    }

    return columns.map(category => {
      if (category.fixedColumnOrder || !columnOrder || columnOrder === "default") {
        return category
      }

      return {
        ...category,
        columns: category.columns.toSorted((columnA, columnB) => {
          if (!columnA) {
            return A_AFTER_B
          }
          if (!columnB) {
            return A_BEFORE_B
          }

          const valueA = Math.abs(columnA.getter
            ? columnA.getter(aggregatedRow, columnA.path)
            : get(aggregatedRow, columnA.path))

          const valueB = Math.abs(columnB.getter
            ? columnB.getter(aggregatedRow, columnB.path)
            : get(aggregatedRow, columnB.path))

          if (isNaN(valueA)) {
            return A_AFTER_B
          }
          if (isNaN(valueB)) {
            return A_BEFORE_B
          }

          if (valueA === valueB) {
            if (columnA.label > columnB.label) {
              return A_AFTER_B
            }
            return A_BEFORE_B
          }

          return valueB - valueA
        }),
      }
    })
  }, [columns, aggregatedRow, columnOrder])

  const tableRef = useRef()
  // We're using useResizeObserver here because this logic needs to be executed if the table resizes, something useEffect or useLayoutEffect won't do
  useResizeObserver(tableRef, () => handleResizeObserver(tableRef, numberOfStickyColumns))

  const hasStickyColumns = recursiveStickyCheck(sortedColumns)
  return <>
    <div className="components_material-table" ref={ref} onScroll={onScroll}>
      <MUITable id={id} ref={tableRef} onClick={onClick} className={classNames(className, { disableCategories })}>
        <TableHead className="header">
          {!disableCategories && <TableRow size="small" id={`${id}-categories`}>
            <TableCell key="group_id" size="small" className="group-end sticky-left" />
            {map(sortedColumns, ({ id, category, categoryTooltip, columns, color }, index) =>
              <TableCell key={`group_${index}-${id || snakeCase(category)}`} className={classNames("superheader group-start", { "sticky-left": some(columns, ({ isSticky }) => isSticky) })} colSpan={size(columns)} align="center" style={{ color }}>
                {category || "\u00A0"}
                {categoryTooltip && <CellTooltip contents={[categoryTooltip]} />}
              </TableCell>,
            )}
          </TableRow>}
          <TableRow id={`${id}-columns`}>
            <TableCell className={classNames("group-end", { "sticky-left": hasStickyColumns })} size="small">
              {idCell}
            </TableCell>
            {map(sortedColumns, ({ category, columns }) => map(columns, (column, index) => {
              const { id, type, label, sorted, sortGetter, allowAbsSort, absSorted, color, tooltipContents, adjustedReason, isSticky, tooltip, favorite } = column
              return <TableCell key={`${category}${id}`} data-test-class={`${kebabCase(label)}-label`} size="small" align="right" sortDirection={sorted} className={classNames("column-label", { "group-start": index === 0, "sticky-left": isSticky })}>
                <SortLabelWithTooltip
                  tooltip={tooltip}
                  noSortLabels={noSortLabels}
                  absolute={absSorted}
                  style={{ color }}
                  active={!!sorted}
                  direction={sorted || DESC}
                  favorite={favorite}
                  className="ellipsable"
                  classes={{ icon: classNames({ "abs-order-icon": absSorted, "custom-color-order-icon": !absSorted && !!color }) }}
                  onClick={() => setSort({
                    type,
                    id,
                    getter: sortGetter,
                    ...getNextSort({ direction: sorted, absolute: absSorted }, allowAbsSort),
                  })}
                >
                  {isFunction(label) ? label(isActive) : label}
                </SortLabelWithTooltip>
                {adjustedReason && <OPTooltip title={adjustedReason}><OfflineBolt fontSize="small" className="column-adjusted-icon" /></OPTooltip>}
                {tooltipContents && <CellTooltip color={color} contents={tooltipContents} />}
              </TableCell>
            }),
            )}
          </TableRow>
          {aggregatedRow
            && <Row
              key={aggregatedRow.id}
              disableLink={aggregatedRow.disableLink}
              item={aggregatedRow}
              columns={sortedColumns}
              disableRowSelection={disableRowSelection}
              dispatch={dispatch}
              groupDropdown={groupDropdown}
              isActive={isActive}
            />}
        </TableHead>
        <TableBody>
          {children || (status === "ok" && map(foundSecurities, (security) => {
            return (<Row
              disableCategories
              key={`${security.id}`}
              openSecurityProfile={openSecurityProfile}
              openFactorProfile={openFactorProfile}
              openCompositeSecurity={openCompositeSecurity}
              rowIdClickOverride={rowIdClickOverride}
              selected={!!selectedSecurities[security.id]}
              item={security}
              columns={sortedColumns}
              onSelect={selectSecurity}
              disableRowSelection={disableRowSelection}
              dispatch={dispatch}
              isActive={isActive}
              onRowClick={onRowClick}
              onChange={onChange}
            />)
          }))}
        </TableBody>
        {(count > 0 && !outsidePagination) && <TableFooter>
          <TableRow className="pagination-row">
            <TablePagination
              rowsPerPageOptions={[]}
              count={count}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={setPage}
            />
          </TableRow>
        </TableFooter>}
      </MUITable>
    </div>
    {(count > 0 && outsidePagination) && <div className="components_material-table_outside-pagination">
      <TablePagination
        component="div"
        className="outside-pagination"
        rowsPerPageOptions={[]}
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={setPage}
      />
    </div>}
  </>
})

Table.propTypes = {
  selectAllSecurities: PropTypes.func,
  foundSecurities: PropTypes.array,
  columnOrder: PropTypes.string,
  columns: PropTypes.array.isRequired,
  currentPageSelectionStatus: PropTypes.string,
  setSort: PropTypes.func.isRequired,
  count: PropTypes.number,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  status: PropTypes.string,
  selectSecurity: PropTypes.func,
  selectedSecurities: PropTypes.object.isRequired,
  openSecurityProfile: PropTypes.func.isRequired,
  openFactorProfile: PropTypes.func.isRequired,
  openCompositeSecurity: PropTypes.func.isRequired,
  disableColumnsSelection: PropTypes.bool,
  disableRowSelection: PropTypes.bool,
  allowIdSort: PropTypes.bool,
  idSort: PropTypes.string,
  aggregatedRow: PropTypes.object,
  rowIdClickOverride: PropTypes.func,
  groupDropdown: TABLE_GROUP_DROPDOWN,
  noSortLabels: PropTypes.bool,
  dispatch: PropTypes.func,
  disableCategories: PropTypes.bool,
  isActive: PropTypes.bool,
  outsidePagination: PropTypes.bool,
  onRowClick: PropTypes.func,
  onChange: PropTypes.func,
  className: PropTypes.string,
  rowsPerPage: PropTypes.number,
  onScroll: PropTypes.func,
  onClick: PropTypes.func,
  id: PropTypes.string,
}

Table.displayName = "Table"

export default Table
